import * as tslib_1 from "tslib";
import { ErrorHandler } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Airport } from './models/airport.model';
import { Business } from '../business/models/business.model';
import { BusinessSummary } from './models/business-summary.model';
import { AirportAutocompleteResult } from '../shared/models/airport-autocomplete-result.model';
import { AirportSearchResult } from './models/airport-search-result.model';
import { ChangeType, TerminalProcedureBatchFormat, TerminalProcedurePack } from './models/terminal-procedure.model';
import { NOTAM } from './models/notam.model';
/**
 * Minimum distance when performing a radius search (nautical miles)
 */
export var MIN_RADIUS = 25;
/**
 * Maximum distance when performing a radius search (nautical miles)
 */
export var MAX_RADIUS = 500;
/**
 * Maximum area of bounding box (in square degrees) for airport bounding box search.
 */
export var MAX_BBOX_AREA = 150;
/**
 * File size limit for the terminal procedure batch script (100MB).
 */
export var MAX_PROC_BATCH_FILESIZE = 100e6;
/**
 * Maximum number of terminal procedures the user is allowed to download in a single batch.
 */
export var MAX_PROC_BATCH_LIMIT = 200;
var AirportService = /** @class */ (function () {
    function AirportService(_http, _decimalPipe, _errorHandler) {
        this._http = _http;
        this._decimalPipe = _decimalPipe;
        this._errorHandler = _errorHandler;
        /**
         * Airports cached by identifier.
         * @private
         */
        this._airportCache = new Map();
    }
    /**
     * Returns a sanitized search term for airport search, with all invalid characters removed.
     * @param {string} term Original input term
     * @returns {string} Sanitized term
     */
    AirportService.sanitizeSearchTerm = function (term) {
        return (term || '').toString().replace(/[^a-z0-9 ]+/gi, '').trim();
    };
    /**
     * Returns true when a term could be an airport ICAO/FAA identifier (as opposed to our internal numeric
     * airport id). FAA/ICAO identifiers always contain at least one letter, so an all-digit term is only ever
     * an internal id. The public search boxes use this to look up airports by identifier only — a user should
     * not be able to jump to an airport by typing its internal numeric id.
     * @param {string} term Original input term
     * @returns {boolean}
     */
    AirportService.isAirportIdentifier = function (term) {
        var t = (term || '').trim();
        return t.length > 0 && !/^\d+$/.test(t);
    };
    /**
     * Returns true if we should show the fees section for the given business. This is based on a heuristic that
     * tries to determine if the given business is a traditional FBO that provides line services and/or fuel.
     * @param {Business | BusinessSummary} business
     * @returns {boolean}
     */
    AirportService.shouldShowFees = function (business) {
        if (business instanceof Business) {
            return (business.services && business.services.length > 0)
                || (business.overviewFees && business.overviewFees.length > 0)
                || (business.fuel && business.fuel.length > 0);
        }
        if (business instanceof BusinessSummary) {
            return business.hasOverviewFees || (business.fuel && business.fuel.length > 0);
        }
        return false;
    };
    /**
     * Gets an airport from the API by identifier
     * @param identifier {String} Airport identifier (FAA or ICAO)
     * @param [reload] {Boolean} Force reload airport (ignore cache)
     * @returns {Promise<Airport>} Airport promise
     */
    AirportService.prototype.getAirport = function (identifier, reload) {
        if (reload === void 0) { reload = false; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, airport, e_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        identifier = (identifier || '').trim().toUpperCase();
                        if (!identifier) {
                            return [2 /*return*/, null];
                        }
                        if (!reload && this._airportCache.has(identifier)) {
                            return [2 /*return*/, this._airportCache.get(identifier)];
                        }
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/airports/" + identifier).toPromise()];
                    case 1:
                        response = _a.sent();
                        airport = Airport.parse(response);
                        this._cacheAirport(airport);
                        return [2 /*return*/, airport];
                    case 2:
                        e_1 = _a.sent();
                        if (e_1 && e_1.status === 404) {
                            return [2 /*return*/, null];
                        }
                        this._errorHandler.handleError(e_1);
                        throw e_1;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    AirportService.prototype._cacheAirport = function (airport) {
        if (airport.icaoId) {
            this._airportCache.set(airport.icaoId, airport);
        }
        if (airport.faaId) {
            this._airportCache.set(airport.faaId, airport);
        }
    };
    /**
     * Returns airport search results for a given search term. The search term may match on airport identifier
     * (FAA or ICAO), name, or city.
     * @param term {string} Search term
     * @param [includePrivate] {boolean} Whether to include private use airports in the results (defaults to false).
     * @returns {Promise<AirportSearchResult[]>} List of matching airport results
     */
    AirportService.prototype.getAirportsBySearchTerm = function (term, includePrivate) {
        if (includePrivate === void 0) { includePrivate = false; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, e_2;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        term = AirportService.sanitizeSearchTerm(term);
                        if (term.length < 3) {
                            throw new Error('At least three alphanumeric characters are necessary to perform a search.');
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/search/advanced", {
                                searchType: 'Basic',
                                searchTerm: "%" + term + "%",
                                'private': includePrivate
                            }).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [2 /*return*/, response.map(AirportSearchResult.parse)];
                    case 3:
                        e_2 = _a.sent();
                        this._errorHandler.handleError(e_2);
                        throw e_2;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns airport search results for a comma separated list of airport IDs.
     * @param airportList {string} Search term
     * @param [includePrivate] {boolean} Whether to include private use airports in the results (defaults to false).
     * @returns {Promise<AirportSearchResult[]>} List of matching airport results
     */
    AirportService.prototype.getFavoriteAirports = function (airportList, includePrivate) {
        if (includePrivate === void 0) { includePrivate = false; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, e_3;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (airportList.length < 3) {
                            throw new Error('At least three alphanumeric characters are necessary to perform a search.');
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/search/advanced", {
                                searchType: 'Favorites',
                                searchTerm: airportList,
                                'private': includePrivate
                            }).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [2 /*return*/, response.map(AirportSearchResult.parse)];
                    case 3:
                        e_3 = _a.sent();
                        this._errorHandler.handleError(e_3);
                        throw e_3;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Performs a radial search for airports within a given distance of a center point.
     * @param latlon {LatLon} Center point of search
     * @param radius {number} Radius in nautical miles
     * @param [includePrivate] {boolean} Whether to include private use airports in the results (defaults to false).
     * @returns {Promise<AirportSearchResult[]>} List of matching airport results
     */
    AirportService.prototype.getAirportsByLatLon = function (latlon, radius, includePrivate) {
        if (includePrivate === void 0) { includePrivate = false; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, e_4;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!latlon) {
                            throw new Error('latlon is required');
                        }
                        if (!radius || radius < MIN_RADIUS || radius > MAX_RADIUS) {
                            throw new Error("radius is required and must be between " + MIN_RADIUS + " and " + MAX_RADIUS + " nautical miles.");
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/search/advanced", {
                                searchType: 'Radius',
                                latitude: latlon.lat,
                                longitude: latlon.lon,
                                radius: radius,
                                'private': includePrivate
                            }).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [2 /*return*/, response.map(AirportSearchResult.parse)];
                    case 3:
                        e_4 = _a.sent();
                        this._errorHandler.handleError(e_4);
                        throw e_4;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Performs a bounding box search for airports.
     * @param bounds {BoundingBox} Bounding box
     * @param [includePrivate] {boolean} Whether to include private use airports in the results (defaults to false).
     * @returns {Promise<AirportSearchResult[]>} List of matching airport results
     */
    AirportService.prototype.getAirportsByBoundingBox = function (bounds, includePrivate) {
        if (includePrivate === void 0) { includePrivate = false; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var area, neLat, neLon, swLat, swLon, response, e_5;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        area = bounds.getAreaDegrees();
                        if (area > MAX_BBOX_AREA) {
                            throw new Error("Bounding box area (" + area + " square degrees) exceeds maximum (" + MAX_BBOX_AREA + ").");
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        neLat = bounds.ne.lat.toFixed(7);
                        neLon = bounds.ne.lon.toFixed(7);
                        swLat = bounds.sw.lat.toFixed(7);
                        swLon = bounds.sw.lon.toFixed(7);
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/search/advanced", {
                                searchType: 'Rectangle',
                                searchTerm: neLat + "," + neLon + "," + swLat + "," + swLon,
                                'private': includePrivate
                            }).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [2 /*return*/, response.map(AirportSearchResult.parse)];
                    case 3:
                        e_5 = _a.sent();
                        this._errorHandler.handleError(e_5);
                        throw e_5;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns all airports within a given state
     * @param state {string} State code
     * @param [includePrivate] {boolean} Whether to include private use airports in the results (defaults to false).
     * @returns {Promise<AirportSearchResult[]>} List of matching airport results
     */
    AirportService.prototype.getAirportsByState = function (state, includePrivate) {
        if (includePrivate === void 0) { includePrivate = false; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, e_6;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!state) {
                            throw new Error('"state" parameter is required when searching airports by state');
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.post(environment.airportsApiUrl + "/search/advanced", {
                                searchType: 'State',
                                searchTerm: state,
                                'private': includePrivate
                            }).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [2 /*return*/, response.map(AirportSearchResult.parse)];
                    case 3:
                        e_6 = _a.sent();
                        this._errorHandler.handleError(e_6);
                        throw e_6;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns terminal procedure data (current and next cycle) for a given array of airport FAA identifiers.
     * @param faaIds {Array<string>} FAA identifiers for the airports for which to return terminal procedures
     * @param [includeDeleted] {boolean} Whether to include deleted procedures in the results (defaults to false).
     * @returns {Promise<TerminalProcedurePack>} Terminal procedure data
     */
    AirportService.prototype.getTerminalProcedures = function (faaIds, includeDeleted) {
        if (includeDeleted === void 0) { includeDeleted = false; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var ids, response, result, e_7;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!faaIds || faaIds.length < 1) {
                            return [2 /*return*/, null];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        ids = faaIds.join(',');
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/procedures?airports=" + ids).toPromise()];
                    case 2:
                        response = _a.sent();
                        result = TerminalProcedurePack.parse(response.terminalProcedures, response.terminalProceduresNext);
                        if (!includeDeleted) {
                            result.current.procedures = result.current.procedures.filter(function (p) { return p.changeType !== ChangeType.Deleted; });
                            result.next.procedures = result.next.procedures.filter(function (p) { return p.changeType !== ChangeType.Deleted; });
                        }
                        return [2 /*return*/, result];
                    case 3:
                        e_7 = _a.sent();
                        this._errorHandler.handleError(e_7);
                        throw e_7;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns a URL that can be used to download a set of terminal procedures, either as a single PDF or ZIP file.
     * @param procedures {Array<TerminalProcedure>} Terminal procedures to download
     * @param format {TerminalProcedureBatchFormat} Format
     * @returns {string} URL that can be used to download the procedure batch.
     */
    AirportService.prototype.getProcedureBatchUrl = function (procedures, format) {
        if (procedures.length < 1) {
            return null;
        }
        var script;
        switch (format) {
            case TerminalProcedureBatchFormat.ZIP:
                script = 'zipbatch';
                break;
            case TerminalProcedureBatchFormat.PDF:
                script = 'pdfbatch';
                break;
            default:
                throw new Error("Unsupported instrument procedure file type: \"" + format + "\"");
        }
        var procList = procedures.map(function (p) { return p.id; }).join(',');
        return environment.airportsApiUrl + "/procedures/" + script + "?p=" + procList;
    };
    /**
     * Gets the details of a business from the API by ID
     * @param id {String} Business ID
     * @returns {Promise<Business>} Business
     */
    AirportService.prototype.getBusiness = function (id) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, e_8;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/businesses/" + id).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, Business.parse(response)];
                    case 2:
                        e_8 = _a.sent();
                        if (e_8 && e_8.status === 404) {
                            return [2 /*return*/, null];
                        }
                        this._errorHandler.handleError(e_8);
                        throw e_8;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Gets airport autocomplete search results for the given query
     * @param query {String} Search query
     * @param [max] {number} max number to be returned by this service.  If API returns more records they will be removed.
     *                       Default is 10 records.
     * @returns {Observable<>} Airport promise
     */
    AirportService.prototype.getAirportCompletions = function (query, max) {
        if (max === void 0) { max = 10; }
        query = (query || '').toString().replace(/[^a-z0-9 ,]+/gi, '').trim();
        if (query.length < 3) {
            return of([]);
        }
        try {
            return this._http
                .get(environment.airportsApiUrl + "/airportsautocomplete/" + query)
                .pipe(map(function (d) { return d.map(function (r) { return AirportAutocompleteResult.parse(r); }).slice(0, max); }));
        }
        catch (e) {
            if (e && e.status === 404) {
                return null;
            }
            this._errorHandler.handleError(e);
            throw e;
        }
    };
    AirportService.prototype.getAirportNOTAMs = function (FaaId) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, e_9;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!FaaId) {
                            throw new Error('FaaId is required');
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.get(environment.airportsApiUrl + "/airports/notams/" + FaaId).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [2 /*return*/, response.map(NOTAM.parse)];
                    case 3:
                        e_9 = _a.sent();
                        this._errorHandler.handleError(e_9);
                        throw e_9;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    return AirportService;
}());
export { AirportService };
